import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { ArrowLeft, Plane, Download, Check, Upload } from 'lucide-react';

const STATUS_VARIANT = { pending: 'warning', booked: 'success', cancelled: 'default' };

export default function TravelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    employeeApi.getTravel(id)
      .then((res) => setRequest(res.request))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex items-center justify-center py-20"><div className="h-6 w-6 animate-spin rounded-full border-[2.5px] border-border border-t-primary" /></div>;
  if (!request) return <div className="text-center py-20 text-muted-foreground">Travel request not found.</div>;

  const r = request;
  const hasPrefs = r.flightPref1Airline || r.flightPref2Airline || r.flightPref3Airline;

  // Parse preferences — stored as JSON string. Each preference has 1+
  // segments; multi-segment preferences = separate bookings for one option.
  let preferences = [];
  try {
    if (r.preferences) preferences = typeof r.preferences === 'string' ? JSON.parse(r.preferences) : r.preferences;
  } catch { preferences = []; }

  // Flat flightPreferences (legacy fallback for older requests).
  let flatPrefs = [];
  try {
    if (r.flightPreferences) flatPrefs = typeof r.flightPreferences === 'string' ? JSON.parse(r.flightPreferences) : r.flightPreferences;
  } catch { flatPrefs = []; }

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/travel')} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors cursor-pointer">
        <ArrowLeft className="h-4 w-4" /> Back to Travel
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{r.departureLocation} → {r.finalDestination}</h1>
          <p className="text-muted-foreground">{r.departureDate} to {r.arrivalDate}</p>
        </div>
        <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Travel Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Departure</div><div className="font-medium">{r.departureLocation}</div></div>
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Destination</div><div className="font-medium">{r.finalDestination}</div></div>
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Departure Date</div><div>{r.departureDate}</div></div>
              <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Arrival Date</div><div>{r.arrivalDate}</div></div>
              {r.reason && <div className="col-span-2"><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Reason</div><div>{r.reason}</div></div>}
            </div>
            {r.groupId && (
              <div className="mt-4 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                Group trip &middot; requested by {r.requestedByName || '—'}
              </div>
            )}
            {r.passportUrl && (
              <a href={r.passportUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-4 rounded-lg bg-muted px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent transition-colors">
                <Download className="h-3.5 w-3.5" /> Download Passport
              </a>
            )}
            {/* A colleague who organised this trip usually can't supply your
                passport, so you attach your own here. Only while pending —
                once booked the travel team already has what they need. */}
            {!r.passportUrl && r.status === 'pending' && (
              <div className="mt-4">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-warning/40 bg-warning/5 px-3 py-2 text-xs font-medium text-foreground hover:bg-warning/10 transition-colors">
                  <Upload className="h-3.5 w-3.5" />
                  {uploading ? 'Uploading…' : 'Upload your passport'}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    disabled={uploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append('passport', file);
                        await employeeApi.uploadTravelPassport(r.rowKey, fd);
                        toast.success('Passport uploaded');
                        const res = await employeeApi.getTravel(id);
                        setRequest(res.request || res);
                      } catch (err) {
                        toast.error(err.message);
                      } finally {
                        setUploading(false);
                      }
                    }}
                  />
                </label>
                <p className="mt-1 text-xs text-muted-foreground">
                  The travel team needs this before they can book your ticket.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {preferences.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Flight Preferences</CardTitle>
              <CardDescription>
                {r.tripType === 'round_trip' ? 'Round-trip · ' : ''}
                {preferences.length === 1 ? '1 preferred option' : `${preferences.length} preferred options`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {preferences.map((pref, prefIdx) => {
                  const isRoundTrip = r.tripType === 'round_trip' && Array.isArray(pref.returnSegments) && pref.returnSegments.length > 0;
                  return (
                    <div key={prefIdx} className="rounded-lg border border-border p-3">
                      <div className="text-sm font-semibold mb-2">
                        Preference {prefIdx + 1}
                        {isRoundTrip && (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">(round-trip)</span>
                        )}
                      </div>
                      <SegmentBlock heading={isRoundTrip ? 'Outbound' : null} segments={pref.segments} />
                      {isRoundTrip && (
                        <div className="mt-3">
                          <SegmentBlock heading="Return" segments={pref.returnSegments} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {preferences.length === 0 && (hasPrefs || flatPrefs.length > 0) && (
          <Card>
            <CardHeader><CardTitle>Flight Preferences</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(flatPrefs.length > 0
                  ? flatPrefs
                  : [1, 2, 3]
                      .map((n) => ({ airline: r[`flightPref${n}Airline`], number: r[`flightPref${n}Number`] }))
                      .filter((p) => p.airline || p.number)
                ).map((p, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm">
                    <span className="text-muted-foreground">{idx + 1}.</span>
                    <span className="font-medium">{p.airline}</span>
                    {p.number && <span className="font-mono text-xs text-muted-foreground">{p.number}</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {r.status === 'booked' && (
          <Card className="border-success/20 bg-success/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Check className="h-5 w-5 text-success" /> Booking Confirmed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Airline</div><div className="font-medium">{r.bookedAirline}</div></div>
                <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Flight</div><div className="font-mono font-medium">{r.bookedFlightNumber}</div></div>
                {r.bookedDepartureTime && <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Departure</div><div>{r.bookedDepartureTime}</div></div>}
                {r.bookedArrivalTime && <div><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Arrival</div><div>{r.bookedArrivalTime}</div></div>}
                {r.bookingReference && <div className="col-span-2"><div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Booking Reference</div><div className="font-mono font-bold text-lg">{r.bookingReference}</div></div>}
              </div>
              {r.bookingTicketUrl && (
                <a href={r.bookingTicketUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-4 rounded-lg bg-success/10 px-3 py-1.5 text-xs font-medium text-success hover:bg-success/20 transition-colors">
                  <Download className="h-3.5 w-3.5" /> Download Booking Ticket {r.bookingTicketFileName ? `(${r.bookingTicketFileName})` : ''}
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {r.status === 'pending' && (
          <Card className="border-warning/20 bg-warning/5">
            <CardContent className="py-4 text-sm text-warning">
              Your travel request is being processed. The office manager has been notified and will arrange booking.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function SegmentBlock({ heading, segments }) {
  if (!Array.isArray(segments) || segments.length === 0) return null;
  return (
    <div className="space-y-2">
      {heading && (
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {heading}
          {segments.length > 1 && (
            <span className="ml-2 font-normal normal-case">({segments.length} separate bookings)</span>
          )}
        </div>
      )}
      {segments.map((s, si) => {
        const segLabel = segments.length === 1
          ? null
          : si === 0 ? 'First booking'
          : si === segments.length - 1 ? 'Final booking'
          : `Booking ${si + 1}`;
        return (
          <div key={si} className="rounded-md border border-border/60 bg-muted/20 p-2 text-sm">
            {segLabel && (
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
                <Plane className="h-3 w-3" /> {segLabel}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium">{s.airline || '—'}</span>
              {s.number && <span className="font-mono text-xs text-muted-foreground">{s.number}</span>}
              {(s.from || s.to) && (
                <span className="text-muted-foreground">{s.from || '—'} → {s.to || '—'}</span>
              )}
              {(s.date || s.time) && <span className="text-xs text-muted-foreground">{[s.date, s.time].filter(Boolean).join(' ')}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
